import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'

import { connect } from 'react-redux'
import { isEqual } from 'lodash'

import { actions as snactions } from 'store/snackbar'
import { selectScid } from 'store/college'

import { DEFAULT_USER_TYPE } from 'components/SegmentSelector/UserTypeSelector'
import {
  saveExistingOutreachMessage,
  saveNewOutreachMessage,
  ingestMessage,
} from 'components/ComposeOutreachMessageButton/services/message-service'
import { Context } from './Context'
import EditModal, { MODE_CREATE, MODE_EDIT, MODE_EDIT_DRAFT, MODE_COPY } from './EditModal'
import { defaultDateTomorrow } from './services/helpers'

const getModalMode = (mode, msg) => {
  if (mode === 'new') {
    return MODE_CREATE
  }
  if (mode === 'edit') {
    if (msg.IsDraft) {
      return MODE_EDIT_DRAFT
    }
    return MODE_EDIT
  }
  if (mode === 'copy') {
    return MODE_COPY
  }
}

export const ContextWrapper = ({ scid, children, saveFn, saveErrorText, onSave, addSnackbarItem }) => {
  const [mode, setMode] = useState('new')
  const [open, setOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [messageOriginal, setMessageOriginal] = useState({})
  const [messageUpdated, setMessageUpdated] = useState({})

  const showModal = useCallback((record, mode) => {
    const ingestedMessage = ingestMessage(record, DEFAULT_USER_TYPE)
    const deepCopyIngestedMessage = { ...ingestedMessage }
    setMessageOriginal(record)
    if (mode === 'copy') {
      // Copy the original message removing identifiers
      delete deepCopyIngestedMessage.Id
      delete deepCopyIngestedMessage.CreatedDate
      delete deepCopyIngestedMessage.CreatedById
      deepCopyIngestedMessage.IsDraft = true
      // Only set the scheduled date if it's a one time message
      if (deepCopyIngestedMessage.ScheduledDate) {
        deepCopyIngestedMessage.ScheduledDate = defaultDateTomorrow()
      }
      // If automated disabled message, reenable it
      if (deepCopyIngestedMessage.Disabled) {
        deepCopyIngestedMessage.Disabled = false
      }
    }
    setMessageUpdated(deepCopyIngestedMessage)
    setMode(mode)
    setOpen(true)
  }, [])

  const saveMessage = useCallback(
    (publish) => {
      const msg = {
        ...messageUpdated,
        IsDraft: !publish,
      }
      setIsSaving(true)
      saveFn(scid, msg, messageOriginal)
        .then(() => {
          setOpen(false)
          setIsSaving(false)
          setTimeout(() => onSave(), 100)
        })
        .catch(() => {
          setIsSaving(false)
          addSnackbarItem(saveErrorText)
        })
    },
    [messageUpdated, messageOriginal, scid, saveFn, saveErrorText, onSave, addSnackbarItem],
  )

  return (
    <Context.Provider value={{ showModal }}>
      {children}
      <EditModal
        show={open}
        mode={getModalMode(mode, messageOriginal)}
        message={messageUpdated}
        setMessage={setMessageUpdated}
        messageHasChanged={() => !isEqual(messageOriginal, messageUpdated)}
        onHide={() => setOpen(false)}
        onSave={saveMessage}
        isSaving={isSaving}
      />
    </Context.Provider>
  )
}

ContextWrapper.propTypes = {
  scid: PropTypes.number,
  children: PropTypes.node.isRequired,
  onSave: PropTypes.func.isRequired,
  saveFn: PropTypes.func.isRequired,
  saveErrorText: PropTypes.string.isRequired,
  addSnackbarItem: PropTypes.func,
}

export const ConnectedContextWrapper = connect(
  (store) => ({
    scid: selectScid(store),
  }),
  {
    addSnackbarItem: snactions.addSnackbarItem,
  },
)(ContextWrapper)

export const ComposeContextWrapper = ({ children, onSave }) => (
  <ConnectedContextWrapper
    saveFn={saveNewOutreachMessage}
    saveErrorText="An error occurred adding your message."
    onSave={onSave}
  >
    {children}
  </ConnectedContextWrapper>
)

ComposeContextWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  onSave: PropTypes.func.isRequired,
}

export const EditContextWrapper = ({ children, onSave }) => (
  <ConnectedContextWrapper
    saveFn={saveExistingOutreachMessage}
    saveErrorText="An error occurred editing your message."
    onSave={onSave}
  >
    {children}
  </ConnectedContextWrapper>
)

EditContextWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  onSave: PropTypes.func.isRequired,
}
